import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  BookOpen, Video, FileText, Dumbbell, 
  Apple, Brain, Play 
} from "lucide-react";

const CATEGORIES = [
  { id: 'all', label: 'All', icon: BookOpen },
  { id: 'fitness', label: 'Fitness', icon: Dumbbell },
  { id: 'nutrition', label: 'Nutrition', icon: Apple },
  { id: 'mental_health', label: 'Mental Health', icon: Brain },
];

const Content = () => {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [contentType, setContentType] = useState(null);

  useEffect(() => {
    fetchContent();
  }, [category, contentType]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      let params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (contentType) params.append('content_type', contentType);
      
      const response = await axios.get(
        `${API}/content${params.toString() ? '?' + params.toString() : ''}`, 
        { withCredentials: true }
      );
      setContent(response.data);
    } catch (error) {
      console.error("Fetch content error:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'fitness': return <Dumbbell className="w-4 h-4" />;
      case 'nutrition': return <Apple className="w-4 h-4" />;
      case 'mental_health': return <Brain className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'session': return <Play className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      
      {/* Header */}
      <header className="px-6 pt-6 pb-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-serif text-sage-darker mb-2">Learn & Grow</h1>
          <p className="text-muted-foreground text-sm">Expert content for your wellness journey</p>
        </div>
      </header>

      {/* Category Tabs */}
      <div className="px-6 mb-6">
        <div className="max-w-4xl mx-auto">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="bg-white border border-sage/20 p-1 h-auto">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger 
                    key={cat.id} 
                    value={cat.id}
                    className="data-[state=active]:bg-sage data-[state=active]:text-white rounded-lg px-4 py-2"
                    data-testid={`category-${cat.id}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content Type Filter */}
      <div className="px-6 mb-6">
        <div className="max-w-4xl mx-auto flex gap-2">
          <Button 
            variant={contentType === null ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType(null)}
            className={`rounded-full ${contentType === null ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="type-all"
          >
            All Types
          </Button>
          <Button 
            variant={contentType === 'article' ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType('article')}
            className={`rounded-full ${contentType === 'article' ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="type-article"
          >
            <FileText className="w-4 h-4 mr-1" />
            Articles
          </Button>
          <Button 
            variant={contentType === 'video' ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType('video')}
            className={`rounded-full ${contentType === 'video' ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="type-video"
          >
            <Video className="w-4 h-4 mr-1" />
            Videos
          </Button>
        </div>
      </div>

      {/* Content Grid */}
      <main className="px-6" data-testid="content-main">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="bg-white border-sage/20 animate-pulse">
                  <CardContent className="p-0">
                    <div className="h-40 bg-sage/10" />
                    <div className="p-4 space-y-2">
                      <div className="h-5 bg-sage/10 rounded w-3/4" />
                      <div className="h-4 bg-sage/10 rounded w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : content.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-sage/40 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-sage-darker mb-2">No content found</h3>
              <p className="text-muted-foreground">Check back later for new articles and videos</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {content.map((item) => (
                <Card 
                  key={item.content_id} 
                  className="bg-white border-sage/20 hover:shadow-lg transition-shadow overflow-hidden group"
                  data-testid={`content-${item.content_id}`}
                >
                  <CardContent className="p-0">
                    {/* Content Image */}
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={item.image_url || "https://images.unsplash.com/photo-1554042967-bb63c815a74c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
                      
                      {/* Type badge */}
                      <div className="absolute top-4 left-4 flex gap-2">
                        <Badge className="bg-white/90 text-sage-darker backdrop-blur-sm">
                          {getTypeIcon(item.content_type)}
                          <span className="ml-1 capitalize">{item.content_type}</span>
                        </Badge>
                      </div>

                      {/* Play button for videos */}
                      {item.content_type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 bg-sage/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="w-6 h-6 text-white fill-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Content Info */}
                    <div className="p-4 space-y-2">
                      <Badge variant="outline" className={`text-xs ${
                        item.category === 'fitness' ? 'border-blue-200 text-blue-600' :
                        item.category === 'nutrition' ? 'border-green-200 text-green-600' :
                        item.category === 'mental_health' ? 'border-purple-200 text-purple-600' :
                        'border-sage/30 text-sage-darker'
                      }`}>
                        {getCategoryIcon(item.category)}
                        <span className="ml-1 capitalize">{item.category.replace('_', ' ')}</span>
                      </Badge>
                      <h3 className="text-sage-darker font-medium line-clamp-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">{item.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Content;
